import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { StandardTitle } from './StandardTitle';
import { calculateUnitLoadTotals } from '@/lib/palletCalculations';

const styles = StyleSheet.create({
    container: {
        marginBottom: 15,
    },
    palletTitle: {
        fontSize: 16,
        color: '#333',
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 15,
    },
    palletDetailsCard: {
        backgroundColor: '#ffffff',
        borderRadius: 6,
        padding: 12,
        borderWidth: 1,
        borderColor: '#e0e0e0',
        marginBottom: 15,
    },
    cardTitle: {
        fontSize: 14,
        color: '#004899',
        fontWeight: 'bold',
        marginBottom: 8,
    },
    cardGrid: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    fieldGroup: {
        width: '50%',
        marginBottom: 8,
        paddingRight: 8,
    },
    fieldLabel: {
        fontSize: 8,
        color: '#666',
        marginBottom: 2,
        fontWeight: 'bold',
    },
    fieldValue: {
        fontSize: 9,
        color: '#333',
        backgroundColor: '#f7f7f7',
        padding: 6,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: '#e0e0e0',
    },
    accordionSection: {
        marginBottom: 12,
        borderWidth: 1,
        borderColor: '#e0e0e0',
        borderRadius: 6,
        overflow: 'hidden',
    },
    accordionHeader: {
        backgroundColor: '#f8f9fa',
        padding: 8,
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    accordionTitle: {
        fontSize: 12,
        color: '#004899',
        fontWeight: 'bold',
    },
    accordionContent: {
        padding: 10,
        backgroundColor: '#ffffff',
    },
    categoryTitle: {
        fontSize: 10,
        color: '#004899',
        fontWeight: 'bold',
        marginBottom: 6,
        marginTop: 4,
    },
    yellowField: {
        backgroundColor: '#fffbeb',
        borderColor: '#fef3c7',
    },
    grayField: {
        backgroundColor: '#f9fafb',
        borderColor: '#f3f4f6',
    },
    noDataText: {
        fontSize: 10,
        color: '#666',
        fontStyle: 'italic',
        padding: 10,
    }
});

interface PalletConfigurationSectionProps {
    pallet: any;
}

const firstRecord = (...values: any[]) => {
    const records = values.flatMap(value => Array.isArray(value) ? value.slice(0, 1) : [value])
        .filter(value => value && typeof value === 'object' && Object.keys(value).length > 0);
    return Object.assign({}, ...records.reverse());
};

const firstValue = (...values: any[]) => values.find(value => {
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return true;
});

const formatShippingModes = (pallet: any, palletDetails: any) => {
    const modes = firstValue(pallet.shippingModes, pallet.palletShippingModes, palletDetails.shippingModes);
    if (!Array.isArray(modes) || modes.length === 0) return 'N/A';

    return modes.map((mode: any, index: number) => {
        const modeNumber = firstValue(mode?.mode, mode?.shippingMode?.mode, mode?.shippingModeId, mode?.id, index + 1);
        return `Mode ${modeNumber}`;
    }).join(', ');
};

export const PalletConfigurationSection: React.FC<PalletConfigurationSectionProps> = ({ pallet }) => {
    // Access the data directly from the pallet object based on response structure
    const primaryDetails = firstRecord(pallet.primaryPackaging, pallet.primaryPackagingDetails);
    const secondaryDetails = firstRecord(pallet.secondaryPackaging, pallet.secondaryPackagingDetails);
    const tertiaryDetails = firstRecord(pallet.tertiaryPackaging, pallet.tertiaryPackagingDetails);
    const palletSpecs = firstRecord(pallet.palletDetails);
    const unitLoadDetails = firstRecord(pallet.unitLoadDetails);
    const palletizingRecipe = firstRecord(pallet.palletizingRecipe, pallet.palletizingRecipes);
    const numberOfPallets = firstValue(pallet.numberOfPallets, palletSpecs.numberOfPallets);
    const calculatedTotals = calculateUnitLoadTotals({
        unitLoadDetails,
        secondaryPackagingDetails: secondaryDetails,
        tertiaryPackagingDetails: tertiaryDetails,
    });
    const totalCasesPerUnitLoad = firstValue(
        unitLoadDetails.totalCasesPerUnitLoad,
        calculatedTotals.totalCasesPerUnitLoad,
    );
    const itemsPerPallet = firstValue(unitLoadDetails.itemsPerPallet, calculatedTotals.itemsPerPallet);

    return (
        <View style={styles.container}>
            <StandardTitle title={`Pallet Configuration - ${pallet.variant} - ${pallet.name}`} />

            {/* Pallet Details Card */}
            <View style={styles.palletDetailsCard}>
                <Text style={styles.cardTitle}>Pallet Details</Text>
                <View style={styles.cardGrid}>
                    <View style={styles.fieldGroup}>
                        <Text style={styles.fieldLabel}>Pallet Variant</Text>
                        <Text style={styles.fieldValue}>{pallet.variant || 'N/A'}</Text>
                    </View>

                    <View style={styles.fieldGroup}>
                        <Text style={styles.fieldLabel}>Pallet Name</Text>
                        <Text style={styles.fieldValue}>{pallet.name || 'N/A'}</Text>
                    </View>

                    <View style={styles.fieldGroup}>
                        <Text style={styles.fieldLabel}>Shipping Modes</Text>
                        <Text style={styles.fieldValue}>
                            {formatShippingModes(pallet, palletSpecs)}
                        </Text>
                    </View>

                    <View style={styles.fieldGroup}>
                        <Text style={styles.fieldLabel}>Number of Pallets</Text>
                        <Text style={styles.fieldValue}>{numberOfPallets ?? 'N/A'}</Text>
                    </View>
                </View>
            </View>

            {/* Primary Packaging Details Accordion */}
            <View style={styles.accordionSection}>
                <View style={styles.accordionHeader}>
                    <Text style={styles.accordionTitle}>Primary Packaging Details</Text>
                </View>
                <View style={styles.accordionContent}>
                    <View style={styles.cardGrid}>
                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Package Type</Text>
                            <Text style={styles.fieldValue}>{primaryDetails.packageType || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Category</Text>
                            <Text style={styles.fieldValue}>{primaryDetails.category || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Brand</Text>
                            <Text style={styles.fieldValue}>{primaryDetails.brand || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Application</Text>
                            <Text style={styles.fieldValue}>{primaryDetails.application || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Finish Type</Text>
                            <Text style={styles.fieldValue}>{primaryDetails.finishType || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Package Material</Text>
                            <Text style={styles.fieldValue}>{primaryDetails.packageMaterial || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Filled Bottle Top Load</Text>
                            <Text style={styles.fieldValue}>{primaryDetails.filledBottleTopLoad || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Diameter</Text>
                            <Text style={styles.fieldValue}>
                                {primaryDetails.diameter || 'N/A'} {primaryDetails.diameterUnit || ''}
                            </Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Package Height</Text>
                            <Text style={styles.fieldValue}>
                                {primaryDetails.packageHeight || 'N/A'} {primaryDetails.packageHeightUnit || ''}
                            </Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Net Content</Text>
                            <Text style={styles.fieldValue}>
                                {primaryDetails.netContent || 'N/A'} {primaryDetails.netContentUnit || ''}
                            </Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Filled Weight</Text>
                            <Text style={styles.fieldValue}>
                                {primaryDetails.filledWeight || 'N/A'} {primaryDetails.filledWeightUnit || ''}
                            </Text>
                        </View>

                        {primaryDetails.packageType === 'bottle' && (
                            <View style={styles.fieldGroup}>
                                <Text style={styles.fieldLabel}>Bottle Design</Text>
                                <Text style={styles.fieldValue}>{primaryDetails.bottleDesign || 'N/A'}</Text>
                            </View>
                        )}

                        {primaryDetails.packageType === 'can' && (
                            <View style={styles.fieldGroup}>
                                <Text style={styles.fieldLabel}>Can Design</Text>
                                <Text style={styles.fieldValue}>{primaryDetails.canDesign || 'N/A'}</Text>
                            </View>
                        )}
                    </View>
                </View>
            </View>

            {/* Secondary Packaging Details Accordion - Only show if secondaryPkg is true */}
            <View style={styles.accordionSection}>
                <View style={styles.accordionHeader}>
                    <Text style={styles.accordionTitle}>Secondary Packaging Details</Text>
                </View>
                <View style={styles.accordionContent}>
                    {secondaryDetails.secondaryPkg ? (
                        <View style={styles.cardGrid}>
                            <View style={styles.fieldGroup}>
                                <Text style={styles.fieldLabel}>Secondary Package Type</Text>
                                <Text style={styles.fieldValue}>{secondaryDetails.secondaryPackageType || 'N/A'}</Text>
                            </View>

                            <View style={styles.fieldGroup}>
                                <Text style={styles.fieldLabel}>Secondary Package Case</Text>
                                <Text style={styles.fieldValue}>{secondaryDetails.secondaryPackageCase || 'N/A'}</Text>
                            </View>

                            <View style={styles.fieldGroup}>
                                <Text style={styles.fieldLabel}>Secondary Unit Net Weight</Text>
                                <Text style={styles.fieldValue}>
                                    {secondaryDetails.secondaryUnitNetWeight || 'N/A'} {secondaryDetails.secondaryUnitNetWeightUnit || ''}
                                </Text>
                            </View>

                            <View style={styles.fieldGroup}>
                                <Text style={styles.fieldLabel}>Secondary Unit Gross Weight</Text>
                                <Text style={styles.fieldValue}>
                                    {secondaryDetails.secondaryUnitGrossWeight || 'N/A'} {secondaryDetails.secondaryUnitGrossWeightUnit || ''}
                                </Text>
                            </View>

                            <View style={styles.fieldGroup}>
                                <Text style={styles.fieldLabel}>Secondary Case Length</Text>
                                <Text style={styles.fieldValue}>
                                    {secondaryDetails.secondaryCaseLength || 'N/A'} {secondaryDetails.secondaryCaseLengthUnit || ''}
                                </Text>
                            </View>

                            <View style={styles.fieldGroup}>
                                <Text style={styles.fieldLabel}>Secondary Case Width</Text>
                                <Text style={styles.fieldValue}>
                                    {secondaryDetails.secondaryCaseWidth || 'N/A'} {secondaryDetails.secondaryCaseWidthUnit || ''}
                                </Text>
                            </View>

                            <View style={styles.fieldGroup}>
                                <Text style={styles.fieldLabel}>Secondary Case Height</Text>
                                <Text style={styles.fieldValue}>
                                    {secondaryDetails.secondaryCaseHeight || 'N/A'} {secondaryDetails.secondaryCaseHeightUnit || ''}
                                </Text>
                            </View>

                            <View style={styles.fieldGroup}>
                                <Text style={styles.fieldLabel}>Filled Case Compression</Text>
                                <Text style={styles.fieldValue}>{secondaryDetails.filledCaseCompression || 'N/A'}</Text>
                            </View>
                        </View>
                    ) : (
                        <Text style={styles.noDataText}>Secondary packaging not configured for this pallet.</Text>
                    )}
                </View>
            </View>

            {/* Tertiary Packaging Details Accordion - Only show if tertiaryPkg is true */}
            <View style={styles.accordionSection}>
                <View style={styles.accordionHeader}>
                    <Text style={styles.accordionTitle}>Tertiary Packaging Details</Text>
                </View>
                <View style={styles.accordionContent}>
                    {tertiaryDetails.tertiaryPkg ? (
                        <View style={styles.cardGrid}>
                            <View style={styles.fieldGroup}>
                                <Text style={styles.fieldLabel}>Tertiary Package Type</Text>
                                <Text style={styles.fieldValue}>{tertiaryDetails.tertiaryPackageType || 'N/A'}</Text>
                            </View>

                            <View style={styles.fieldGroup}>
                                <Text style={styles.fieldLabel}>Secondary Packages Per Unit</Text>
                                <Text style={styles.fieldValue}>{firstValue(tertiaryDetails.tertiarySecondaryPackagesPerUnit, tertiaryDetails.tertiarySecondaryPackagesPerUn) ?? 'N/A'}</Text>
                            </View>

                            <View style={styles.fieldGroup}>
                                <Text style={styles.fieldLabel}>Tertiary Unit Net Weight</Text>
                                <Text style={styles.fieldValue}>
                                    {tertiaryDetails.tertiaryUnitNetWeight || 'N/A'} {tertiaryDetails.tertiaryUnitNetWeightUnit || ''}
                                </Text>
                            </View>

                            <View style={styles.fieldGroup}>
                                <Text style={styles.fieldLabel}>Tertiary Unit Gross Weight</Text>
                                <Text style={styles.fieldValue}>
                                    {tertiaryDetails.tertiaryUnitGrossWeight || 'N/A'} {tertiaryDetails.tertiaryUnitGrossWeightUnit || ''}
                                </Text>
                            </View>

                            <View style={styles.fieldGroup}>
                                <Text style={styles.fieldLabel}>Tertiary Case Length</Text>
                                <Text style={styles.fieldValue}>
                                    {tertiaryDetails.tertiaryCaseLength || 'N/A'} {tertiaryDetails.tertiaryCaseLengthUnit || ''}
                                </Text>
                            </View>

                            <View style={styles.fieldGroup}>
                                <Text style={styles.fieldLabel}>Tertiary Case Width</Text>
                                <Text style={styles.fieldValue}>
                                    {tertiaryDetails.tertiaryCaseWidth || 'N/A'} {tertiaryDetails.tertiaryCaseWidthUnit || ''}
                                </Text>
                            </View>

                            <View style={styles.fieldGroup}>
                                <Text style={styles.fieldLabel}>Tertiary Case Height</Text>
                                <Text style={styles.fieldValue}>
                                    {tertiaryDetails.tertiaryCaseHeight || 'N/A'} {tertiaryDetails.tertiaryCaseHeightUnit || ''}
                                </Text>
                            </View>
                        </View>
                    ) : (
                        <Text style={styles.noDataText}>Tertiary packaging not configured for this pallet.</Text>
                    )}
                </View>
            </View>

            {/* Pallet Details Accordion */}
            <View style={styles.accordionSection}>
                <View style={styles.accordionHeader}>
                    <Text style={styles.accordionTitle}>Pallet Specifications</Text>
                </View>
                <View style={styles.accordionContent}>
                    <View style={styles.cardGrid}>
                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Pallet Types</Text>
                            <Text style={styles.fieldValue}>{palletSpecs.palletTypes || 'N/A'}</Text>
                        </View>
                    </View>
                </View>
            </View>

            {/* Unit Load Details Accordion */}
            <View style={styles.accordionSection}>
                <View style={styles.accordionHeader}>
                    <Text style={styles.accordionTitle}>Unit Load Details</Text>
                </View>
                <View style={styles.accordionContent}>
                    <View style={styles.cardGrid}>
                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Unit Load Length</Text>
                            <Text style={styles.fieldValue}>
                                {unitLoadDetails.unitLoadLength || 'N/A'} {unitLoadDetails.unitLoadLengthUnit || ''}
                            </Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Unit Load Width</Text>
                            <Text style={styles.fieldValue}>
                                {unitLoadDetails.unitLoadWidth || 'N/A'} {unitLoadDetails.unitLoadWidthUnit || ''}
                            </Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Unit Load Height</Text>
                            <Text style={styles.fieldValue}>
                                {unitLoadDetails.unitLoadHeight || 'N/A'} {unitLoadDetails.unitLoadHeightUnit || ''}
                            </Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Unit Load Weight</Text>
                            <Text style={styles.fieldValue}>
                                {unitLoadDetails.unitLoadWeight || 'N/A'} {unitLoadDetails.unitLoadWeightUnit || ''}
                            </Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Unit Cases Per Layer</Text>
                            <Text style={styles.fieldValue}>{unitLoadDetails.unitCasesPerLayer || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Unit Layers Per Unit Load</Text>
                            <Text style={styles.fieldValue}>{unitLoadDetails.unitLayersPerUnitLoad || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Machine Assembled</Text>
                            <Text style={styles.fieldValue}>{unitLoadDetails.machineAssembled || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Plant</Text>
                            <Text style={styles.fieldValue}>{unitLoadDetails.plant || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Plant Line</Text>
                            <Text style={styles.fieldValue}>{unitLoadDetails.plantLine || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Eng Help</Text>
                            <Text style={styles.fieldValue}>{unitLoadDetails.engHelp || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Variety Pack</Text>
                            <Text style={styles.fieldValue}>{unitLoadDetails.varietyPack || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Total Cases Per Unit Load</Text>
                            <Text style={styles.fieldValue}>{totalCasesPerUnitLoad ?? 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Items Per Pallet</Text>
                            <Text style={styles.fieldValue}>{itemsPerPallet ?? 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Pallet Pattern Design</Text>
                            <Text style={styles.fieldValue}>{unitLoadDetails.palletPatternDesign || 'N/A'}</Text>
                        </View>
                    </View>
                </View>
            </View>

            {/* Palletizing Recipe Accordion */}
            <View style={styles.accordionSection}>
                <View style={styles.accordionHeader}>
                    <Text style={styles.accordionTitle}>Palletizing Recipe</Text>
                </View>
                <View style={styles.accordionContent}>
                    <View style={styles.cardGrid}>
                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Stretch Ga</Text>
                            <Text style={styles.fieldValue}>{palletizingRecipe.stretchGa || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Stretch Width</Text>
                            <Text style={styles.fieldValue}>
                                {palletizingRecipe.stretchWidth || 'N/A'} {palletizingRecipe.stretchWidthUnit || ''}
                            </Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Number of Revs Top</Text>
                            <Text style={styles.fieldValue}>{palletizingRecipe.numberOfRevsTop || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Number of Revs Middle</Text>
                            <Text style={styles.fieldValue}>{palletizingRecipe.numberOfRevsMiddle || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Number of Revs Bottom</Text>
                            <Text style={styles.fieldValue}>{palletizingRecipe.numberOfRevsBottom || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Cut and Weigh</Text>
                            <Text style={styles.fieldValue}>{palletizingRecipe.cutAndWeigh || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Pre Stretch</Text>
                            <Text style={styles.fieldValue}>{palletizingRecipe.preStretch || 'N/A'}</Text>
                        </View>

                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>Stretch Wrap Spec Number</Text>
                            <Text style={styles.fieldValue}>{palletizingRecipe.stretchWrapSpecNumber || 'N/A'}</Text>
                        </View>
                    </View>
                </View>
            </View>
        </View>
    );
};
